import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { MIconMenu } from 'library';
import ErrorIcon from '@mui/icons-material/Error';
import SubdirectoryArrowLeftRoundedIcon from '@mui/icons-material/SubdirectoryArrowLeftRounded';
import { useUserRolePermissions } from 'hooks';
import { func, number, string } from 'prop-types';
import { format } from 'date-fns';
import { formatNumberWithoutDecimals } from 'utils/helpers/format';
import * as R from 'ramda';
import { AuthContext } from 'authentication/AuthContext';
import { batchStatusTooltips, isEditableStatus } from './utils';
import StatusChip from './components/StatusChip';

const titleStyling = {
  fontWeight: 400,
};

const gridStyle = {
  display: 'grid',
  gridGap: 8,
  gridTemplateAreas: {
    lg: `'main hr status acceptance origin product volume accessCode date action'`,
    xs: `
      'main action'
      'acceptance volume'
      'origin date'
      'product accessCode'
      `,
  },
  gridTemplateColumns: {
    lg: '1.25fr 0.2fr repeat(2, 0.75fr) repeat(5, 1fr) 0.1fr',
    xs: '1fr 1fr',
  },
  gridTemplateRows: { lg: '1fr', xs: 'repeat(4)' },
};

const BatchesCard = ({
  acceptance,
  accessCode,
  availableDate,
  batchId,
  batchOwnerName,
  onCancel,
  onCopy,
  originName,
  originOwnerName,
  productCode,
  status,
  volume,
}) => {
  const { isProductShippingManage } = useUserRolePermissions();
  const { affiliates } = useContext(AuthContext);
  const navigate = useNavigate();

  const viewMenu = [
    {
      label: 'View Batch',
      onClick: () => {
        navigate(`./${batchId}/edit`);
      },
      value: 'view',
    },
  ];
  const editMenu = [
    {
      label: 'Edit Batch',
      onClick: () => {
        navigate(`./${batchId}/edit`);
      },
      value: 'edit',
    },
  ];
  const copyCancelMenu = [
    {
      label: 'Copy Batch',
      // eslint-disable-next-line no-shadow
      onClick: batchId => {
        onCopy('batch', batchId);
      },
      value: batchId,
    },
    {
      label: 'Cancel Batch',
      onClick: () => {
        onCancel('batch', batchId);
      },
      value: 'cancel',
    },
  ];

  const isBatchOwnerAndManager = R.and(
    affiliates.some(({ customerNameShort }) => R.equals(customerNameShort, batchOwnerName)),
    isProductShippingManage
  );

  const menuItems = R.and(isBatchOwnerAndManager, isEditableStatus(status))
    ? R.concat(editMenu, copyCancelMenu)
    : !isEditableStatus(status) && isBatchOwnerAndManager
    ? R.concat(viewMenu, copyCancelMenu.slice(0, 1))
    : viewMenu;

  return (
    <Card sx={{ '&:last-child': { mb: 0 }, mb: 2 }}>
      <CardContent
        sx={{
          p: { lg: 3, xs: 2 },
        }}
      >
        <Box sx={gridStyle}>
          <Stack spacing={0.5} sx={{ gridArea: 'main', gridColumn: 1, mb: { lg: 0, xs: 1 } }}>
            <Typography sx={titleStyling} variant="caption">
              #{batchId}
            </Typography>
            <Typography sx={{ fontWeight: 700 }} variant="body1">
              {batchOwnerName}
            </Typography>
            <Stack direction="row" spacing={0.5}>
              <SubdirectoryArrowLeftRoundedIcon
                sx={{ color: 'graphic.dark', fontSize: 16, transform: 'rotate(90deg)' }}
              />
              <Typography sx={{ fontWeight: 400, pt: '2px' }} variant="caption">
                {originOwnerName}
              </Typography>
            </Stack>
            <Box sx={{ display: { lg: 'none', xs: 'block' } }}>
              <Tooltip
                enterTouchDelay={10}
                placement="top"
                title={batchStatusTooltips[status] || ''}
              >
                <StatusChip status={status} />
              </Tooltip>
            </Box>
          </Stack>
          <Box
            sx={{
              borderLeft: 1,
              borderLeftColor: 'graphic.light',
              display: { lg: 'block', xs: 'none' },
              gridArea: 'hr',
              gridColumn: 2,
            }}
          />
          <Stack spacing={0.5} sx={{ display: { lg: 'flex', xs: 'none' }, gridArea: 'status' }}>
            <Typography
              sx={{ ...titleStyling, display: { lg: 'inline', xs: 'none' } }}
              variant="caption"
            >
              Status
            </Typography>
            <Tooltip enterTouchDelay={10} placement="top" title={batchStatusTooltips[status] || ''}>
              <StatusChip status={status} />
            </Tooltip>
          </Stack>
          <Stack spacing={0.5} sx={{ gridArea: 'acceptance' }}>
            <Typography sx={titleStyling} variant="caption">
              Acceptance
            </Typography>
            {R.equals('0 of 0', acceptance) ? (
              <Tooltip
                enterTouchDelay={10}
                placement="bottom"
                slotProps={{
                  popper: { sx: { maxWidth: 200, p: 1, textAlign: 'center' } },
                }}
                title="There are no shipments linked to this batch"
              >
                <ErrorIcon sx={{ color: 'warning.main' }} />
              </Tooltip>
            ) : (
              <Typography variant="body2">{acceptance}</Typography>
            )}
          </Stack>
          <Stack spacing={0.5} sx={{ gridArea: 'origin' }}>
            <Typography sx={titleStyling} variant="caption">
              Origin
            </Typography>
            <Typography variant="body2">{originName}</Typography>
          </Stack>
          <Stack spacing={0.5} sx={{ gridArea: 'product' }}>
            <Typography sx={titleStyling} variant="caption">
              Product
            </Typography>
            <Typography variant="body2">{productCode}</Typography>
          </Stack>
          <Stack spacing={0.5} sx={{ gridArea: 'volume' }}>
            <Typography sx={titleStyling} variant="caption">
              Volume
            </Typography>
            <Typography variant="body2">{formatNumberWithoutDecimals(volume)} bbl</Typography>
          </Stack>
          <Stack spacing={0.5} sx={{ gridArea: 'accessCode' }}>
            <Typography sx={titleStyling} variant="caption">
              Access Code
            </Typography>
            <Typography variant="body2">{accessCode}</Typography>
          </Stack>
          <Stack spacing={0.5} sx={{ gridArea: 'date' }}>
            <Typography sx={titleStyling} variant="caption">
              Available Date
            </Typography>
            <Typography variant="body2">
              {format(Date.parse(availableDate), 'MMMM d, yyyy')}
            </Typography>
          </Stack>
          <Box sx={{ gridArea: 'action', justifySelf: 'end' }}>
            <MIconMenu items={menuItems} />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

BatchesCard.propTypes = {
  acceptance: string.isRequired,
  accessCode: string.isRequired,
  availableDate: string.isRequired,
  batchId: number.isRequired,
  batchOwnerName: string.isRequired,
  onCancel: func.isRequired,
  onCopy: func.isRequired,
  originName: string.isRequired,
  originOwnerName: string.isRequired,
  productCode: string.isRequired,
  status: string.isRequired,
  volume: number.isRequired,
};

export default BatchesCard;
